'use client';

import React, { useState } from 'react';
import type { Product } from '@/lib/supabaseService';
import AppImage from '@/components/ui/AppImage';
import ProductStoryModal from './ProductStoryModal';

interface ProductCardProps {
  product: Product;
  selectedSizeId: string | null;
  quantity: number;
  onSizeSelect: (
    productId: string,
    productName: string,
    sizeId: string,
    sizeLabel: string,
    unitPrice: number
  ) => void;
  onQuantityChange: (productId: string, delta: number) => void;
}

export default function ProductCard({
  product,
  selectedSizeId,
  quantity,
  onSizeSelect,
  onQuantityChange,
}: ProductCardProps) {
  const [storyOpen, setStoryOpen] = useState(false);

  // A product is "all sold out" only if every size has stock=0 AND is not unlimited
  const allSoldOut = product.sizes.every((s) => !s.unlimitedInventory && s.stock === 0);
  const selectedSize = product.sizes.find((s) => s.id === selectedSizeId);
  const isUnlimited = selectedSize?.unlimitedInventory ?? false;
  const maxStock = isUnlimited ? Infinity : (selectedSize?.stock ?? 0);
  const atMaxStock = !isUnlimited && quantity >= maxStock;

  return (
    <>
      <div className={`card-product flex flex-col${allSoldOut ? ' opacity-60' : ''}`}>
        {/* Image */}
        <div className="relative w-full" style={{ paddingBottom: '65%' }}>
          <div className="absolute inset-0">
            <AppImage
              src={product.image}
              alt={product.imageAlt}
              fill
              sizes="(max-width: 640px) 100vw, 50vw"
              className="object-cover"
            />
            {allSoldOut && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(255,246,233,0.75)' }}
              >
                <span
                  className="text-sm font-bold tracking-wide px-3 py-1 rounded-full"
                  style={{
                    backgroundColor: 'var(--primary)',
                    color: 'var(--primary-foreground)',
                  }}
                >
                  Sold Out
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-2.5 flex flex-col gap-2 flex-1">
          {/* Name + Know More */}
          <div className="flex items-start justify-between gap-1">
            <h3 className="font-serif text-sm font-semibold leading-tight" style={{ color: 'var(--foreground)' }}>
              {product.name}
            </h3>
            <button
              onClick={() => setStoryOpen(true)}
              className="text-xs font-medium shrink-0 underline underline-offset-2"
              style={{ color: 'var(--secondary)', fontSize: '0.65rem' }}
            >
              Know more
            </button>
          </div>

          <p className="leading-relaxed" style={{ color: 'var(--muted-foreground)', fontSize: '0.65rem' }}>
            {product.description}
          </p>

          {/* Size selector */}
          {!allSoldOut && (
            <div>
              <p className="font-medium mb-1" style={{ color: 'var(--muted-foreground)', fontSize: '0.65rem' }}>
                Choose size
              </p>
              <div className="flex flex-wrap gap-1">
                {product.sizes.map((size) => {
                  const isSelected = selectedSizeId === size.id;
                  const isSoldOut = !size.unlimitedInventory && size.stock === 0;
                  return (
                    <button
                      key={size.id}
                      disabled={isSoldOut}
                      onClick={() =>
                        !isSoldOut &&
                        onSizeSelect(
                          product.id,
                          product.name,
                          size.id,
                          size.label,
                          size.discountPrice ?? size.regularPrice
                        )
                      }
                      className={`size-btn${isSelected ? ' selected' : ''}`}
                      title={isSoldOut ? 'Sold out' : size.label}
                    >
                      {size.label}
                      {isSoldOut && (
                        <span className="ml-1 text-xs">✕</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Price display for selected size */}
          {selectedSize && (
            <div className="flex items-center gap-1.5">
              <span
                className="text-sm font-bold font-serif"
                style={{ color: 'var(--primary)' }}
              >
                ₹{selectedSize.discountPrice ?? selectedSize.regularPrice}
              </span>
              {selectedSize.discountPrice !== null && (
                <span
                  style={{ color: 'var(--muted-foreground)', fontSize: '0.65rem', textDecoration: 'line-through' }}
                >
                  ₹{selectedSize.regularPrice}
                </span>
              )}
              {/* Low stock warning — only for non-unlimited sizes */}
              {!selectedSize.unlimitedInventory && selectedSize.stock > 0 && selectedSize.stock <= 5 && (
                <span style={{ color: '#B45309', fontSize: '0.65rem', fontWeight: 500, marginLeft: '2px' }}>
                  Only {selectedSize.stock} left
                </span>
              )}
            </div>
          )}

          {/* Price display for non-selected (show range) */}
          {!selectedSize && !allSoldOut && (
            <div>
              <p style={{ color: 'var(--muted-foreground)', fontSize: '0.65rem' }}>
                From{' '}
                <span className="font-semibold" style={{ color: 'var(--primary)' }}>
                  ₹
                  {Math.min(
                    ...product.sizes
                      .filter((s) => s.unlimitedInventory || s.stock > 0)
                      .map((s) => s.discountPrice ?? s.regularPrice)
                  )}
                </span>
              </p>
            </div>
          )}

          {/* Quantity counter — only after size selected */}
          {selectedSizeId && quantity > 0 && !allSoldOut && (
            <div className="flex items-center gap-2 mt-0.5">
              <span style={{ color: 'var(--muted-foreground)', fontSize: '0.65rem', fontWeight: 500 }}>
                Qty
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  className="qty-btn"
                  onClick={() => onQuantityChange(product.id, -1)}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span
                  className="font-bold w-5 text-center tabular-nums"
                  style={{ color: 'var(--foreground)', fontSize: '0.75rem' }}
                >
                  {quantity}
                </span>
                <button
                  className="qty-btn"
                  onClick={() => !atMaxStock && onQuantityChange(product.id, 1)}
                  disabled={atMaxStock}
                  aria-label="Increase quantity"
                  style={atMaxStock ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                >
                  +
                </button>
              </div>
              {atMaxStock && (
                <span style={{ color: '#B45309', fontSize: '0.65rem' }}>
                  Max stock reached
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {storyOpen && (
        <ProductStoryModal
          name={product.name}
          story={product.story}
          onClose={() => setStoryOpen(false)}
        />
      )}
    </>
  );
}